import React, { useState, useCallback } from 'react';
import './FileRenamer.css';

function FileRenamer({ onBack }) {
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingIndex, setProcessingIndex] = useState(-1);
  const [formatPreferences, setFormatPreferences] = useState([]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => file.type === 'application/pdf'
    );

    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles]);
      setFormatPreferences(prev => [...prev, ...droppedFiles.map(() => 'instrument')]);
      setError(null);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(
      file => file.type === 'application/pdf'
    );

    if (selectedFiles.length > 0) {
      setFiles(prev => [...prev, ...selectedFiles]);
      setFormatPreferences(prev => [...prev, ...selectedFiles.map(() => 'instrument')]);
      setError(null);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setResults(prev => prev.filter((_, i) => i !== index));
    setFormatPreferences(prev => prev.filter((_, i) => i !== index));
  };

  const updateFormatPreference = (index, format) => {
    setFormatPreferences(prev => prev.map((pref, i) => i === index ? format : pref));
    // If we already have results, regenerate the filename with the new format
    if (results[index]?.success) {
      const result = results[index];
      const newFilename = generateFilenameClient(result.extractedData, format);
      updateSuggestedFilename(index, newFilename);
    }
  };

  const generateFilenameClient = (extractedData, format) => {
    if (format === 'liber' && extractedData.liber && extractedData.page) {
      return `Liber ${extractedData.liber}, Page ${extractedData.page}.pdf`;
    }

    // Default to instrument number format
    if (extractedData.instrumentNumber) {
      const formattedInstrumentNo = extractedData.instrumentNumber.replace(/^(Instrument\s*(No\.?|Number|#)?:?\s*)/i, '').trim();
      return `Instrument No. ${formattedInstrumentNo}.pdf`;
    }

    // Fallback to liber/page if available
    if (extractedData.liber && extractedData.page) {
      return `Liber ${extractedData.liber}, Page ${extractedData.page}.pdf`;
    }

    return 'Unknown Document.pdf';
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const analyzeFiles = async () => {
    if (files.length === 0) {
      setError('Please add PDF files to analyze');
      return;
    }

    setLoading(true);
    setError(null);
    const newResults = [];

    for (let i = 0; i < files.length; i++) {
      setProcessingIndex(i);
      const file = files[i];

      try {
        const base64 = await convertToBase64(file);

        const response = await fetch('/api/file-renamer/analyze', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            pdfBase64: base64,
            format: formatPreferences[i] || 'instrument'
          }),
        });

        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const textResponse = await response.text();
          console.error('Non-JSON response received:', textResponse.substring(0, 500));
          throw new Error(`Server returned ${response.status}: ${textResponse.substring(0, 100)}`);
        }

        const data = await response.json();

        if (response.ok && data.success) {
          newResults.push({
            originalName: file.name,
            suggestedFilename: data.suggestedFilename,
            extractedData: data.extractedData,
            success: true
          });
        } else {
          newResults.push({
            originalName: file.name,
            error: data.error || 'Analysis failed',
            success: false
          });
        }
      } catch (err) {
        newResults.push({
          originalName: file.name,
          error: err.message,
          success: false
        });
      }
    }

    setResults(newResults);
    setProcessingIndex(-1);
    setLoading(false);
  };

  const downloadRenamedFiles = async () => {
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      if (result.success && result.suggestedFilename) {
        const file = files[i];
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.suggestedFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  const updateSuggestedFilename = (index, newFilename) => {
    setResults(prev => prev.map((result, i) =>
      i === index ? { ...result, suggestedFilename: newFilename } : result
    ));
  };

  const successCount = results.filter(r => r.success).length;

  return (
    <div className="file-renamer">
      <div className="file-renamer-header">
        <h2>AI Document Renamer for SoftPro</h2>
        <p className="file-renamer-subtitle">
          Upload PDF documents and let AI analyze them to generate proper filenames
          based on Instrument Numbers, Document Types, and Recording Dates.
        </p>
      </div>

      <div className="how-it-works">
        <h3>How It Works</h3>
        <div className="how-it-works-steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <strong>Upload PDFs</strong>
              <p>Drag and drop or select your recorded document PDFs</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <strong>Choose Format</strong>
              <p>Select "Instrument No." or "Liber/Page" format for each document</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <strong>Analyze</strong>
              <p>AI reads the document and extracts recording information</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <strong>Review & Edit</strong>
              <p>Check suggested filenames and make any adjustments needed</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">5</div>
            <div className="step-content">
              <strong>Download</strong>
              <p>Download all files with their new standardized names</p>
            </div>
          </div>
        </div>
      </div>


      <div
        className="drop-zone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <div className="drop-zone-content">
          <div className="drop-icon">📄</div>
          <p className="drop-text">Drag and drop PDF files here</p>
          <p className="drop-subtext">or</p>
          <label className="file-select-button">
            Select Files
            <input
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {files.length > 0 && (
        <div className="files-list">
          <div className="files-list-header">
            <h3>Files to Process ({files.length})</h3>
            <button
              className="analyze-button"
              onClick={analyzeFiles}
              disabled={loading}
            >
              {loading ? `Analyzing ${processingIndex + 1}/${files.length}...` : 'Analyze All Files'}
            </button>
          </div>

          <div className="files-table">
            <div className="files-table-header">
              <div className="col-original">Original Filename</div>
              <div className="col-format">Format</div>
              <div className="col-suggested">Suggested Filename</div>
              <div className="col-status">Status</div>
              <div className="col-actions">Actions</div>
            </div>

            {files.map((file, index) => {
              const result = results[index];
              return (
                <div key={index} className={`files-table-row ${result?.success ? 'success' : result?.error ? 'error' : ''}`}>
                  <div className="col-original">
                    <span className="file-icon">📄</span>
                    {file.name}
                  </div>
                  <div className="col-format">
                    <select
                      value={formatPreferences[index] || 'instrument'}
                      onChange={(e) => updateFormatPreference(index, e.target.value)}
                      disabled={loading}
                      className="format-select"
                    >
                      <option value="instrument">Instrument No.</option>
                      <option value="liber">Liber/Page</option>
                    </select>
                  </div>
                  <div className="col-suggested">
                    {loading && processingIndex === index ? (
                      <span className="analyzing">Analyzing...</span>
                    ) : result?.success ? (
                      <input
                        type="text"
                        value={result.suggestedFilename}
                        onChange={(e) => updateSuggestedFilename(index, e.target.value)}
                        className="filename-input"
                      />
                    ) : result?.error ? (
                      <span className="error-text">{result.error}</span>
                    ) : (
                      <span className="pending">Pending analysis</span>
                    )}
                  </div>
                  <div className="col-status">
                    {loading && processingIndex === index ? (
                      <span className="status-badge analyzing">Analyzing</span>
                    ) : result?.success ? (
                      <span className="status-badge success">Ready</span>
                    ) : result?.error ? (
                      <span className="status-badge error">Error</span>
                    ) : (
                      <span className="status-badge pending">Pending</span>
                    )}
                  </div>
                  <div className="col-actions">
                    <button
                      className="remove-button"
                      onClick={() => removeFile(index)}
                      disabled={loading}
                      title="Remove file"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {results.length > 0 && !loading && (
        <div className="results-actions">
          <div className="results-summary">
            <span className="success-count">{successCount} of {results.length} files ready for download</span>
          </div>
          {successCount > 0 && (
            <button className="download-button" onClick={downloadRenamedFiles}>
              Download Renamed Files ({successCount})
            </button>
          )}
        </div>
      )}

      <div className="naming-rules">
        <h3>SoftPro Naming Rules</h3>
        <div className="rules-grid">
          <div className="rule-item">
            <strong>Format:</strong>
            <code>Instrument No. YYYY-NNNNN DOCTYPE MM-DD-YYYY.pdf</code>
          </div>
          <div className="rule-item">
            <strong>Example:</strong>
            <code>Instrument No. 2024-17113 DED 07-18-2024.pdf</code>
          </div>
          <div className="rule-item">
            <strong>Alternative:</strong>
            <code>Liber NNNN, Page NNN DOCTYPE MM-DD-YYYY.pdf</code>
          </div>
        </div>
        <div className="doc-types">
          <h4>Common Document Type Codes:</h4>
          <div className="doc-types-grid">
            <span><code>DED</code> - Deed</span>
            <span><code>MTG</code> - Mortgage</span>
            <span><code>NCM</code> - Notice of Commencement</span>
            <span><code>AGR</code> - Agreement</span>
            <span><code>LSE</code> - Lease</span>
            <span><code>AGRSBD</code> - Subordination</span>
            <span><code>MLN</code> - Mechanic's Lien</span>
            <span><code>REL</code> - Release</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FileRenamer;
